export const formatCurrency = (
  value: number,
  compact: boolean = false,
  showSymbol: boolean = true
): string => {
  if (showSymbol) {
    return new Intl.NumberFormat('ko-KR', {
      style: 'currency',
      currency: 'KRW',
      notation: compact ? 'compact' : 'standard',
      maximumFractionDigits: 0,
    }).format(value);
  } else {
    return new Intl.NumberFormat('ko-KR', {
      style: 'decimal',
      notation: compact ? 'compact' : 'standard',
      maximumFractionDigits: 0,
    }).format(value);
  }
};

/**
 * 통화 코드 기반 금액 포맷터.
 * - KRW(또는 미지정)이면 기존 formatCurrency와 동일하게 동작한다 (하위 호환).
 * - 그 외 통화는 Intl 통화 포맷을 시도하고, 지원되지 않는 코드면 "코드 + 콤마 숫자"로 대체한다.
 * - 해외 포트폴리오의 현지통화 금액(액면가·자본금 등 집계에 쓰이지 않는 금액) 표시에 사용한다.
 */
export const formatAmount = (
  value: number,
  currencyCode?: string | null,
  compact: boolean = false
): string => {
  const code = (currencyCode || 'KRW').toUpperCase();

  if (code === 'KRW') {
    return formatCurrency(value, compact, true);
  }

  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      notation: compact ? 'compact' : 'standard',
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${code} ${new Intl.NumberFormat('en-US', {
      notation: compact ? 'compact' : 'standard',
      maximumFractionDigits: 2,
    }).format(value)}`;
  }
};

export const formatPercent = (value: number): string => {
  return new Intl.NumberFormat('ko-KR', {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value / 100);
};

export const formatNumber = (num: number): string => {
  return new Intl.NumberFormat('ko-KR').format(num);
}; 

export const formatFileSize = (size: number | null): string => {
  if (size === null) {
    return '';
  }
  
  if (size < 1024) {
    return `${size} bytes`;
  } else if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(0)} KB`;
  } else {
    return `${(size / (1024 * 1024)).toFixed(0)} MB`;
  }
};

export const formatNumberWithCommas = (num: number|string|null|undefined): string => {
  if (num === null || num === undefined || num === '') {
    return '0';
  }
  const numStr = num.toString();
  const hasDecimal = numStr.includes('.');
  const [integerPart, decimalPart] = numStr.split('.');
  const formattedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return hasDecimal ? `${formattedIntegerPart}.${decimalPart}` : formattedIntegerPart;
};


export const roundToDecimals = (value: number, decimals?: number) => {
  decimals = decimals === undefined ? 3 : decimals;
  const division = 10 ** decimals;
  return Math.round(value * division) / division;
};

export const removeSpecialCharacters = (text: string): string => {
  return text.replace(/[^a-zA-Z0-9가-힣]/g, '');
};

export const removeComma = (text: string): string => {
  return text.replace(/,/g, '');
};